/* eslint id-length: 0 */
import { parse, stringify } from 'qs';
import { push } from 'connected-react-router';
import { Map, OrderedSet } from 'immutable';

import * as api from './api';
import * as actions from './actions';
import * as constants from './constants';
import * as helpers from './helpers';
import { utils as containerUtils } from './../container';


/**
 * Changes labels in values to ids
 * @param {Array[]} query
 *
 * @return {Array[]} remapped query with IDs instead of labels in values
 *
 * @example
 * for param payload = [['team', ['A-Team']], ['name', ['bar']]]
 * should return [['text', ['12345']], ['name', ['bar']]]
 */
export const filtersLabelsToIds = (query) => (dispatch, getState) => {
  const state = getState().apps.archives;
  const teamLabels = state.query.getIn(['filters', constants.TEAM_FILTER, 'labels']);
  return query.reduce((acc, [key, val]) => {
    let mappedVal = val;
    if (key === constants.TEAM_FILTER) {
      mappedVal = containerUtils.tryMapLabelsToIds({ items: val, labels: teamLabels });
    }

    return [...acc, [key, mappedVal]];
  }, []);
};

/**
 * Changes labels in values to ids
 * @param {Array[]} query
 *
 * @return {Array[]} remapped query with labels instead of IDs in values
 *
 * @example
 * for param payload = [['text', ['12345']], ['name', ['bar']]]
 * should return [['team', ['A-Team']], ['name', ['bar']]]
 */
export const filtersTryMapIdsToLabels = (query) => (dispatch, getState) => {
  const state = getState().apps.archives;
  const teamLabels = state.query.getIn(['filters', constants.TEAM_FILTER, 'labels']);
  return query.reduce((acc, [key, val]) => {
    let mappedVal = val;
    if (key === constants.TEAM_FILTER) {
      mappedVal = val.map((id) => teamLabels[id]);
    }

    return [...acc, [key, mappedVal]];
  }, []);
};


/**
 * Update browser URL with query
 *
 * @return {Promise} - result of location.search update
 */
export const updateURLWithQuery = (payload) => (dispatch) => {
  const normalizedQuery = helpers.normalizeQueryInputValue(payload.q);
  const noEmptyValues = Object
    .entries({
      ...payload,
      q: helpers.unionQuery({ input: '', filters: normalizedQuery }),
    })
    .reduce((acc, [key, val]) => (val ? { ...acc, [key]: val } : acc), {});
  return dispatch(push({ search: stringify(noEmptyValues) }));
};


/**
 * Updates values of filters inside the store
 * @param {Array[]} payload - array of arrays of normalized filters
 *
 * @example
 * for param payload = [['text', ['foo']], ['name', ['bar']]]
 * should dispatch actions:
 * - remove all values for filters in store without any value
 * - for filter items with values - store those values
 * - if date filter changes - update it
 */
export const selectFiltersFromNormalizedPayload = (payload) => async (dispatch, getState) => {
  const filtersSequence = constants.FILTERS_MAP.concat(constants.XTM_FILTERS_MAP)
    .valueSeq().toArray().map((name) => name.toLowerCase());
  const filterNamesInPayload = payload.map(([name]) => name);
  const filtersNotIncludedInPayload = filtersSequence
    .filter((filterName) => !filterNamesInPayload.includes(filterName));

  const filters = getState().apps.archives.query.get('filters');

  payload
    // we want to update values only for valid filter
    .filter(([name]) => filtersSequence.includes(name))
    .forEach(([name, values]) => {
      let normalizedValues = values;
      if (name === constants.TEAM_FILTER) {
        normalizedValues = containerUtils.tryMapLabelsToIds({
          items: values,
          labels: filters.getIn([constants.TEAM_FILTER, 'labels']),
        });
      }

      normalizedValues = Array.isArray(normalizedValues) ? normalizedValues : [normalizedValues];
      dispatch(actions.updateFacetValue({
        name,
        data: {
          items: OrderedSet(normalizedValues),
        },
      }));
    });

  payload
    .filter(([name]) => name === constants.DATE_FILTER)
    // eslint-disable-next-line no-unused-vars
    .forEach(([name, values]) => {
      dispatch(actions.updateDateFacet(Map({
        startDate: helpers.parseDate(values[0]),
        endDate: helpers.parseDate(values[1]),
      })));
    });

  filtersNotIncludedInPayload
    .forEach((name) => {
      dispatch(actions.updateFacetValue({
        name, data: { items: OrderedSet() },
      }));
    });
};


/**
 * Call api for new archives results
 * @param {Object} query
 * @param {string} query.payload - final query which should be sent to the backend
 * @param {boolean} query.mergeResults - concat or replace previous results?
 *
 * @return {Promise} result of API call
 */
export const search = ({
  payload, mergeResults = false,
}) => (dispatch, getState) => {
  let queryString = payload;
  const query = getState().apps.archives.query.get('query').toJSON();
  const v11Params = {};
  const filters = getState().apps.archives.query.get('filters');
  // org_id must be sent in all requests
  v11Params.org_id = getState().user.get('organization').id;
  v11Params.team_id = filters.getIn(['teams', 'selectedItems']).toArray();
  v11Params.owner = filters.getIn(['owner', 'selectedItems']).toArray();

  const parsedQuery = helpers.normalizeQueryInputValue(payload);
  const queryWithLabels = dispatch(filtersLabelsToIds(parsedQuery));
  queryString = helpers.unionQuery({ input: '', filters: queryWithLabels });

  const parsed = parse({ ...query, q: queryString });

  dispatch(updateURLWithQuery(parsed));
  const parsedForRequest = {
    ...parsed,
    q: helpers.serializeQuery(parsed.q),
    ...v11Params,
  };

  dispatch(actions.searchRequest());
  return api.searchV11(parsedForRequest)
    .then(({ data }) => {
      dispatch(actions.searchSuccess({ ...data, mergeResults }));
      // fetch more, if there is still space for results to show
      // eslint-disable-next-line no-use-before-define
      dispatch(fetchJobsUntilThereIsSpaceOnPage({
        currentPosition: query.page * query.per_page,
        totalResults: data.hits.total,
      }));
    })
    .catch((response) => (dispatch(
      actions.searchFailure(response, { skipErrorPage: true }),
    )));
};


/**
 * Fetch values for filters from API
 * @param {Object} query
 * @param {string} query.name - name of the facet which should be fetched
 *
 * @return {Promise} - result of API call
 *
 * @example
 * for param payload = { name: 'builds'}
 * should fetch into the store possible values for builds filter
 */
export const fetchFacet = ({ name, payload }) => (dispatch, getState) => {
  if (name === constants.STATUS_FILTER) {
    return Promise.resolve();
  }
  const filterDate = getState().apps.archives.query.get('filterDate');
  let q = `date: ${helpers.buildDateRange(filterDate.toArray())}`;
  if (payload) {
    q += ` AND ${name}: ${payload}`;
  }
  const parsed = parse({ q });

  dispatch(actions.fetchFacetRequest({ name }));
  return api.fetchFacet({ name, params: parsed }).then(
    ({ data }) => dispatch(actions.fetchFacetSuccess({ facetName: name, ...data })),
    (error) => {
      dispatch(actions.fetchFacetFailure({ name }));
      throw Error(error);
    },
  );
};


/**
 * Based on selected filters value build final query for backend
 * @return {string} - response from unionQuery method
 */
export const buildFullQuery = () => (dispatch, getState) => {
  const state = getState().apps.archives;
  const filters = state.query.get('filters').toJSON();
  const filterDate = state.query.get('filterDate').toJSON();
  const filterTeam = filters[constants.TEAM_FILTER];

  // not available for oTM
  if (filterTeam) {
    filterTeam.selectedItems = containerUtils.tryMapIdsToLabels({
      ids: filterTeam.selectedItems,
      labels: filterTeam.labels,
    });
  }
  const normalizedFilters = helpers.getFiltersWithSelectedItems({
    ...filters,
    date: { selectedItems: [filterDate.startDate, filterDate.endDate] },
  });

  const inputQuery = state.query.get('inputQuery');
  return helpers.unionQuery({ input: inputQuery, filters: normalizedFilters });
};


/**
 * Called when filter from dropdown value is changed.
 * Should update filters, build the query and ask backend for updated data.
 * @return {Promise} - result of API call for new archive results
 */
export const updateFacets = ({ inputQueryRef, payload }) => async (dispatch) => {
  if (payload.name === constants.DATE_FILTER) {
    await dispatch(actions.updateDateFacet(Map(helpers.fullDayRange(
      payload.data.get('startDate'),
      payload.data.get('endDate'),
    ))));
  } else {
    await dispatch(actions.updateFacetValue(payload));
  }

  await dispatch(actions.resetPage());
  const mergedInputQuery = await dispatch(buildFullQuery());
  inputQueryRef.updateValue(mergedInputQuery);
  return dispatch(search({ payload: mergedInputQuery }));
};


/**
 * Called when query input is saved.
 * Should parse updated query input, select filters based on that change, build the query
 * and ask backend for updated data.
 * @param {Object} query
 * @param {Object} query.inputQueryRef - reference to react component of InputQuery
 * @param {string} query.payload - query string
 * }}
 * @return {Promise} - result of API call for new archive results
 */
export const updateViaQueryInput = ({ inputQueryRef, payload }) => async (dispatch) => {
  await dispatch(actions.resetPage());
  await dispatch(actions.updateQuery(payload));
  const parsedQuery = helpers.normalizeQueryInputValue(payload);
  await dispatch(selectFiltersFromNormalizedPayload(parsedQuery));
  const query = await dispatch(buildFullQuery());
  inputQueryRef.updateValue(query);
  return dispatch(search({ payload: query }));
};


/**
 * Save selected sort type
 * and ask backend for updated data.
 *
 * @return {Promise} - result of API call for new archive results
 */
export const saveSortBy = (payload) => async (dispatch) => {
  await dispatch(actions.resetPage());
  await dispatch(actions.updateSort(payload));
  const query = await dispatch(buildFullQuery());
  return dispatch(search({ payload: query }));
};


/**
 * Build store values based on query string from URL
 *
 * @return {Promise} - result of updating input query action
 */
export const getQueryFromURL = () => (dispatch, getState) => {
  const { search: locationSearch } = getState().router.location;
  if (!locationSearch) {
    return Promise.resolve();
  }
  const query = parse(locationSearch, { ignoreQueryPrefix: true }).q;
  const parsedQuery = helpers.normalizeQueryInputValue(query);

  const queryWithLabels = dispatch(filtersTryMapIdsToLabels(parsedQuery));
  const queryString = helpers.unionQuery({ input: '', filters: queryWithLabels });
  dispatch(selectFiltersFromNormalizedPayload(queryWithLabels));
  return Promise.resolve(dispatch(actions.updateQuery(queryString)));
};


/**
 * Show results from the next pagination page
 *
 * @return {Promise} - result of API call for new archive results
 */
export function incrementPage() {
  return async (dispatch) => {
    await dispatch(actions.incrementPage());
    const query = await dispatch(buildFullQuery());
    return dispatch(search({ payload: query, mergeResults: true }));
  };
}


/**
 * Show results from the next pagination page
  @param {Object} query
  @param {Object} query.currentPosition - current position in results
  @param {Object} query.totalResults - number of all results
 */
export const fetchJobsUntilThereIsSpaceOnPage = (
  { currentPosition, totalResults },
) => (dispatch) => {
  // Backend returns 200 OK data: [] if you ask for more results than available, so we
  // have to check first if any results are left
  if (currentPosition >= totalResults) {
    return;
  }
  if (containerUtils.isBottomOfScreenReached()) {
    dispatch(incrementPage());
  }
};
